import React, { useEffect, useState } from "react";
import { FaCheckCircle, FaSearch, FaFilter } from "react-icons/fa";
import api from "../services/api";
import { toast } from "react-toastify";

const Refund = () => {
    const [refunds, setRefunds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("PENDING"); // Default to PENDING
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const fetchRefunds = async () => {
        setLoading(true);
        try {
            const response = await api.getAllRefunds({
                page,
                limit: 10,
                status: statusFilter === "ALL" ? "" : statusFilter
            });

            if (response.success) {
                setRefunds(response.data.refunds);
                setTotalPages(response.data.pagination.totalPages);
            }
        } catch (error) {
            console.error("Error fetching refunds:", error);
            // toast.error("Failed to load refund requests");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRefunds();
    }, [page, statusFilter]);

    const handleApprove = async (refundId) => {
        if (!window.confirm("Are you sure you want to approve this refund? This action handles payment reversal via Razorpay.")) return;

        try {
            const response = await api.approveRefund(refundId);
            if (response.success) {
                toast.success("Refund approved successfully");
                fetchRefunds(); // Refresh list
            }
        } catch (error) {
            console.error("Error approving refund:", error); // Reverted simple generic error
            toast.error(error.message || "Failed to approve refund");
        }
    };

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold mb-6">Refund Requests</h1>

            {/* Filters */}
            <div className="flex gap-4 mb-6 items-center flex-wrap">
                <div className="flex items-center gap-2 bg-white p-2 rounded shadow">
                    <FaFilter className="text-gray-500" />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="outline-none bg-transparent"
                    >
                        <option value="PENDING">Pending</option>
                        <option value="PROCESSED">Processed</option>
                        <option value="ALL">All Status</option>
                    </select>
                </div>

                <button
                    onClick={fetchRefunds}
                    className="ml-auto bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                    Refresh
                </button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="p-4 border-b font-medium text-gray-600">Booking Info</th>
                                <th className="p-4 border-b font-medium text-gray-600">Customer</th>
                                <th className="p-4 border-b font-medium text-gray-600">Amount</th>
                                <th className="p-4 border-b font-medium text-gray-600">Reason</th>
                                <th className="p-4 border-b font-medium text-gray-600">Status</th>
                                <th className="p-4 border-b font-medium text-gray-600">Date</th>
                                <th className="p-4 border-b font-medium text-gray-600">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="p-8 text-center text-gray-500">Loading refunds...</td>
                                </tr>
                            ) : refunds.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="p-8 text-center text-gray-500">No refund requests found.</td>
                                </tr>
                            ) : (
                                refunds.map((refund) => (
                                    <tr key={refund.id} className="hover:bg-gray-50">
                                        <td className="p-4 border-b">
                                            <div className="font-semibold">{refund.booking.serviceName}</div>
                                            <div className="text-xs text-gray-500">{new Date(refund.booking.date).toLocaleDateString()} | {refund.booking.slot}</div>
                                        </td>
                                        <td className="p-4 border-b">
                                            <div className="font-medium">{refund.booking.userName}</div>
                                            <div className="text-xs text-gray-500">{refund.booking.userPhone}</div>
                                        </td>
                                        <td className="p-4 border-b font-semibold">
                                            ₹{refund.amount}
                                        </td>
                                        <td className="p-4 border-b text-sm text-gray-600 max-w-xs truncate" title={refund.reason}>
                                            {refund.reason || "N/A"}
                                        </td>
                                        <td className="p-4 border-b">
                                            <span className={`px-2 py-1 rounded text-xs font-semibold ${refund.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                                refund.status === 'PROCESSED' ? 'bg-green-100 text-green-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                {refund.status}
                                            </span>
                                        </td>
                                        <td className="p-4 border-b text-sm text-gray-500">
                                            {new Date(refund.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="p-4 border-b">
                                            {refund.status === 'PENDING' && (
                                                <button
                                                    onClick={() => handleApprove(refund.id)}
                                                    className="flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded text-sm hover:bg-green-700 transition"
                                                    title="Approve Refund"
                                                >
                                                    <FaCheckCircle /> Approve
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="p-4 border-t flex justify-between items-center bg-gray-50">
                    <button
                        disabled={page === 1}
                        onClick={() => setPage(p => p - 1)}
                        className="px-3 py-1 bg-white border rounded disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-600">Page {page} of {totalPages || 1}</span>
                    <button
                        disabled={page >= totalPages}
                        onClick={() => setPage(p => p + 1)}
                        className="px-3 py-1 bg-white border rounded disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Refund;
